import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../hooks/useTheme';
import { COLORS, FONTS, LAYOUT, SHADOWS } from '../styles/tokens';
import type { AppDefinition } from '../data/apps';
import type { AppFilters } from '../hooks/useAppFilter';

/** SVG sun icon for dark mode toggle */
const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F4C43F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

/** SVG moon icon for light mode toggle */
const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B9BD5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

interface BottomBarProps {
  apps: AppDefinition[];
  filters: AppFilters;
  onFiltersChange: (filters: AppFilters) => void;
  allTeams: string[];
}

/** Bottom bar with POPULAR, FILTERS, and theme toggle */
export function BottomBar({ apps, filters, onFiltersChange, allTeams }: BottomBarProps): React.JSX.Element {
  const { isDark, toggle } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const [showPopular, setShowPopular] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const popularRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);

  /** Close dropdowns on outside click */
  useEffect(() => {
    function handleMouseDown(e: MouseEvent): void {
      if (popularRef.current && !popularRef.current.contains(e.target as Node)) {
        setShowPopular(false);
      }
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setShowFilters(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const onlineApps = apps.filter(a => a.status === 'online').slice(0, 5);

  const pillStyle: React.CSSProperties = {
    height: '28px',
    padding: '0 12px',
    borderRadius: '6px',
    border: `1px solid ${colors.border}`,
    backgroundColor: 'transparent',
    color: isDark ? '#D0D0D0' : '#333333',
    fontFamily: FONTS.heading,
    fontSize: '10px',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'color 0.2s',
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: '36px',
    left: 0,
    minWidth: '200px',
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
    boxShadow: isDark ? SHADOWS.neumorphicDark : SHADOWS.neumorphicLight,
    zIndex: 100,
    overflow: 'hidden',
  };

  function toggleStatus(status: string): void {
    const next = filters.statuses.includes(status)
      ? filters.statuses.filter(s => s !== status)
      : [...filters.statuses, status];
    onFiltersChange({ ...filters, statuses: next });
  }

  function toggleTeam(team: string): void {
    const next = filters.teams.includes(team)
      ? filters.teams.filter(t => t !== team)
      : [...filters.teams, team];
    onFiltersChange({ ...filters, teams: next });
  }

  const hasActiveFilters = filters.statuses.length > 0 || filters.teams.length > 0;

  return (
    <footer
      className="vx-bottom-bar"
      style={{
        height: `${LAYOUT.bottomBarHeight}px`,
        backgroundColor: colors.card,
        borderTop: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        transition: 'background-color 0.3s, border-color 0.3s',
        flexShrink: 0,
      }}
    >
      {/* Left: Label + POPULAR + FILTERS */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
        <span
          className="vx-bottom-brand"
          style={{
            fontFamily: FONTS.body,
            fontSize: '11px',
            color: colors.textSecondary,
            letterSpacing: '0.05em',
          }}
        >
          VERTEX DIGITAL HUB
        </span>

        {/* POPULAR dropdown */}
        <div ref={popularRef} style={{ position: 'relative' }}>
          <button
            style={pillStyle}
            onClick={() => { setShowPopular(p => !p); setShowFilters(false); }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            POPULAR
          </button>
          {showPopular && (
            <div style={dropdownStyle}>
              <div style={{ padding: '8px 0' }}>
                {onlineApps.length === 0 ? (
                  <p style={{ padding: '8px 16px', fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary, margin: 0 }}>
                    No apps available
                  </p>
                ) : (
                  onlineApps.map(app => (
                    <button
                      key={app.id}
                      onClick={() => {
                        if (app.launchUrl !== '#') {
                          window.open(app.launchUrl, '_blank', 'noopener,noreferrer');
                        }
                        setShowPopular(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        padding: '8px 16px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: colors.text,
                        fontFamily: FONTS.body,
                        fontSize: '13px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#4ADE80', flexShrink: 0 }} />
                      {app.name}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* FILTERS dropdown */}
        <div ref={filtersRef} style={{ position: 'relative' }}>
          <button
            style={{
              ...pillStyle,
              color: hasActiveFilters ? COLORS.accent.blue : colors.textSecondary,
              borderColor: hasActiveFilters ? COLORS.accent.blue : colors.border,
            }}
            onClick={() => { setShowFilters(f => !f); setShowPopular(false); }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            FILTERS
            {hasActiveFilters && (
              <span style={{
                width: '16px', height: '16px', borderRadius: '50%',
                backgroundColor: COLORS.accent.blue, color: '#FFF',
                fontFamily: FONTS.body, fontSize: '9px', fontWeight: 700,
                display: 'grid', placeItems: 'center',
              }}>
                {filters.statuses.length + filters.teams.length}
              </span>
            )}
          </button>
          {showFilters && (
            <div style={dropdownStyle}>
              <div style={{ padding: '12px 16px' }}>
                {/* Status filters */}
                <p style={{ fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.12em', color: colors.textSecondary, textTransform: 'uppercase', margin: '0 0 8px' }}>
                  Status
                </p>
                {(['online', 'offline', 'maintenance'] as const).map(status => (
                  <label key={status} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', cursor: 'pointer', fontFamily: FONTS.body, fontSize: '12px', color: colors.text }}>
                    <input
                      type="checkbox"
                      checked={filters.statuses.includes(status)}
                      onChange={() => toggleStatus(status)}
                      style={{ accentColor: COLORS.accent.blue }}
                    />
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </label>
                ))}

                {/* Team filters */}
                {allTeams.length > 0 && (
                  <>
                    <p style={{ fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.12em', color: colors.textSecondary, textTransform: 'uppercase', margin: '12px 0 8px' }}>
                      Team
                    </p>
                    {allTeams.map(team => (
                      <label key={team} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', cursor: 'pointer', fontFamily: FONTS.body, fontSize: '12px', color: colors.text }}>
                        <input
                          type="checkbox"
                          checked={filters.teams.includes(team)}
                          onChange={() => toggleTeam(team)}
                          style={{ accentColor: COLORS.accent.blue }}
                        />
                        {team}
                      </label>
                    ))}
                  </>
                )}

                {/* Clear all */}
                {hasActiveFilters && (
                  <button
                    onClick={() => onFiltersChange({ statuses: [], teams: [] })}
                    style={{
                      marginTop: '10px',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: 'transparent',
                      color: colors.textSecondary,
                      fontFamily: FONTS.body,
                      fontSize: '11px',
                      cursor: 'pointer',
                      width: '100%',
                    }}
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: theme toggle only */}
      <button
        onClick={toggle}
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
          boxShadow: isDark ? SHADOWS.cardDark : SHADOWS.cardLight,
          cursor: 'pointer',
          display: 'grid',
          placeItems: 'center',
          transition: 'background-color 0.3s',
        }}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? <SunIcon /> : <MoonIcon />}
      </button>
    </footer>
  );
}
